import type { BreadcrumbProps } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, matchRoutes, useLocation } from 'react-router';
import type { RouteMatch, RouteObject } from 'react-router';

import { NEW_ENTITY, UPDATE_ENTITY } from 'constants/EntityTitles';
import type { CrumbRoute } from 'constants/EntityTitles';
import { NEW_STEP_KEYS } from 'constants/StepsConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import { getRegisteredRoutes } from 'modules/router/routeRegistry';

type BreadcrumbItems = NonNullable<BreadcrumbProps['items']>;

export type Crumb = {
  path: string;
  label: React.ReactNode;
};

/** True when `param` is really one of `path`'s own `:param` segments (optional `?` marker included). */
export const hasOwnParam = (path: string, param: string): boolean =>
  new RegExp(`(^|/):${param}\\??(?=/|$)`).test(path);

/**
 * True when the route sits on a creation URL.
 *
 * Create and edit share a single route (`/:tagId` matches both `/new` and
 * `/<uuid>`), so the entity param value carries the distinction. The param is
 * declared on the route's `handle.entityIdParam` — enforced at the type level
 * by `RouteHandle`, checked again here in case `handle` was built dynamically.
 */
const isCreationUrl = (match: RouteMatch): boolean => {
  const { handle } = match.route as CrumbRoute;
  const entityIdParam = handle?.entityCrumb ? handle.entityIdParam : undefined;
  const path = match.route.path ?? '';

  if (!entityIdParam) {
    throw new Error(`Route "${path}" has entityCrumb but no entityIdParam declared`);
  }

  if (!hasOwnParam(path, entityIdParam)) {
    throw new Error(`Route "${path}" declares entityIdParam "${entityIdParam}", which is not one of its own params`);
  }

  return Object.keys(NEW_STEP_KEYS).includes(match.params[entityIdParam]);
};

export const buildCrumbsFromMatches = (matches: RouteMatch[]): Crumb[] =>
  matches.reduce<Crumb[]>((acc, match) => {
    const { handle } = match.route as CrumbRoute;

    if (!handle) {
      return acc;
    }

    const { crumb, entityCrumb } = handle;
    const entity = <FormattedMessage {...crumb} />;

    const label = entityCrumb ? (
      <FormattedMessage {...(isCreationUrl(match) ? NEW_ENTITY : UPDATE_ENTITY)} values={{ entity }} />
    ) : (
      entity
    );

    acc.push({ path: match.pathname, label });

    return acc;
  }, []);

const useRouteBreadcrumbItems = (routeObjects: RouteObject[] = getRegisteredRoutes()): MayBe<BreadcrumbItems> => {
  const location = useLocation();
  const matches = React.useMemo<RouteMatch[]>(
    () => matchRoutes(routeObjects, location) ?? [],
    [routeObjects, location]
  );

  return React.useMemo<MayBe<BreadcrumbItems>>(() => {
    const crumbs = buildCrumbsFromMatches(matches);

    if (!crumbs.length) {
      return undefined;
    }

    return crumbs.map(({ path, label }, index) => {
      const isLast = index === crumbs.length - 1;

      return { title: isLast ? label : <Link to={path}>{label}</Link> };
    });
  }, [matches]);
};

export default useRouteBreadcrumbItems;
